import { AUTH_ACTION_TYPES } from '../reducers/authReducer';
import { clearSubscription, syncSubscriptionState } from './subscriptionActions';
import * as authService from '../../services/authService';
import { clearAccessToken } from '../../services/apiClient';
import { postLoginPath } from '../../helpers/flow.helpers';

export const setUser = (user) => ({
  type: AUTH_ACTION_TYPES.SET_USER,
  payload: { user },
});

export const clearAuth = () => ({
  type: AUTH_ACTION_TYPES.CLEAR_AUTH,
});

export const setAuthLoading = (loading) => ({
  type: AUTH_ACTION_TYPES.SET_AUTH_LOADING,
  payload: { loading },
});

export const setAuthError = (error) => ({
  type: AUTH_ACTION_TYPES.SET_AUTH_ERROR,
  payload: { error },
});

export const clearAuthError = () => ({
  type: AUTH_ACTION_TYPES.CLEAR_AUTH_ERROR,
});

export const setBootstrapped = () => ({
  type: AUTH_ACTION_TYPES.SET_BOOTSTRAPPED,
});

export const bootstrapAuth = () => async (dispatch) => {
  dispatch(setAuthLoading(true));
  dispatch(clearAuthError());
  try {
    dispatch(clearAuth());
    dispatch(clearSubscription());

    try {
      const data = await authService.refreshSession();
      authService.markLoginComplete();
      dispatch(setUser(data.user));
      await dispatch(syncSubscriptionState());
    } catch {
      authService.clearLocalSession();
    }
  } catch {
    authService.clearLocalSession();
    dispatch(clearAuth());
    dispatch(clearSubscription());
  } finally {
    dispatch(setBootstrapped());
    dispatch(setAuthLoading(false));
  }
};

/**
 * Google sign-in. Firebase establishes identity; our API verifies the token
 * and owns the session that follows.
 */
const finishGoogle = async (dispatch, getState, idToken, organization, country, acceptTerms) => {
  const data = await authService.loginWithGoogle({ idToken, organization, country, acceptTerms });
  dispatch(setUser(data.user));
  await dispatch(syncSubscriptionState());
  const { subscription } = getState();
  return {
    ...data,
    redirectTo: !data.profileComplete
      ? '/complete-profile'
      : postLoginPath(data.user, subscription.access),
  };
};

export const googleSignIn = ({ idToken, organization, country, acceptTerms } = {}) => async (
  dispatch,
  getState
) => {
  dispatch(setAuthLoading(true));
  dispatch(clearAuthError());
  try {
    return await finishGoogle(dispatch, getState, idToken, organization, country, acceptTerms);
  } catch (e) {
    dispatch(setAuthError(e.message));
    throw e;
  } finally {
    dispatch(setAuthLoading(false));
  }
};

export const proceedAccessGateUser = ({ email, name, organization, country, password }) => async (
  dispatch,
  getState
) => {
  dispatch(setAuthLoading(true));
  dispatch(clearAuthError());
  try {
    const data = await authService.proceedAccessGate({
      email,
      name,
      organization,
      country,
      password,
    });
    if (data.requiresEmailVerification) {
      return data;
    }
    if (!data.isNewUser) {
      dispatch(setUser(data.user));
      await dispatch(syncSubscriptionState());
      const { subscription } = getState();
      const access = subscription.access;
      return {
        ...data,
        redirectTo: postLoginPath(data.user, access),
      };
    }
    return data;
  } catch (e) {
    dispatch(setAuthError(e.message || 'Access failed'));
    throw e;
  } finally {
    dispatch(setAuthLoading(false));
  }
};

export const loginUser = ({ email, password }) => async (dispatch, getState) => {
  dispatch(setAuthLoading(true));
  dispatch(clearAuthError());
  try {
    const data = await authService.login({ email, password });
    dispatch(setUser(data.user));
    await dispatch(syncSubscriptionState());
    const { subscription } = getState();
    const access = subscription.access;
    return {
      ...data,
      redirectTo: postLoginPath(data.user, access),
    };
  } catch (e) {
    dispatch(setAuthError(e.message || 'Login failed'));
    throw e;
  } finally {
    dispatch(setAuthLoading(false));
  }
};

export const registerUser = ({ email, password, name, acceptTerms }) => async (dispatch, getState) => {
  dispatch(setAuthLoading(true));
  dispatch(clearAuthError());
  try {
    const data = await authService.register({ email, password, name, acceptTerms });
    if (data.accessToken) {
      dispatch(setUser(data.user));
      await dispatch(syncSubscriptionState());
      const { subscription } = getState();
      const access = subscription.access;
      return {
        ...data,
        loggedIn: true,
        redirectTo: postLoginPath(data.user, access),
      };
    }
    return { ...data, loggedIn: false };
  } catch (e) {
    dispatch(setAuthError(e.message || 'Registration failed'));
    throw e;
  } finally {
    dispatch(setAuthLoading(false));
  }
};

export const verifyEmailUser = ({ token }) => async (dispatch, getState) => {
  dispatch(setAuthLoading(true));
  dispatch(clearAuthError());
  try {
    const data = await authService.verifyEmail({ token });
    dispatch(setUser(data.user));
    await dispatch(syncSubscriptionState());
    const { subscription } = getState();
    const access = subscription.access;
    return {
      ...data,
      redirectTo: postLoginPath(data.user, access),
    };
  } catch (e) {
    dispatch(setAuthError(e.message || 'Email verification failed'));
    throw e;
  } finally {
    dispatch(setAuthLoading(false));
  }
};

export const logoutUser = () => async (dispatch) => {
  try {
    await authService.logout();
  } catch {
    // still clear local auth
  }
  clearAccessToken();
  authService.clearLocalSession();
  dispatch(clearAuth());
  dispatch(clearSubscription());
};
